/**
 * Created by neo on 18.08.23
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { v1ParamsToV3Params } from './exploreV3Params';
import { ExploreEntryQueryRequest } from './ExploreEntryQueryRequest';
import { SubscribableEntryBase, createSubscribableEntryBase } from './SubscribableEntry';
import { Instructor, createInstructor } from './Instructor';
import { FormField, createFormField } from './FormField';
import { LocalizedValue, createLocalizedValue } from '../LocalizedValue';
import dayjs from 'dayjs';

export type OfflineMeetingLocation = {
  id: string;
  name: string;
  address: string;
};

export type DatePair = {
  startDate: string;
  endDate: string;
  cancelStatus?: string;
};

export type OfflineMeetingEntry = SubscribableEntryBase & {
  type: 'offlineMeeting';
  activityIdentifier?: string;
  data: Record<string, any>;
  language: string;
  previewTitle: LocalizedValue[];
  previewDescription: LocalizedValue[];
  minParticipants: number;
  maxParticipants: number;
  subscriberCount: number;
  registrationFields: FormField[];
  location: OfflineMeetingLocation;
  registerUntilDate?: string | null;
  datePairs: DatePair[];
  readonly remainingSeats: number;
  readonly hasRemainingSeats: boolean;
  readonly isMeetingOver: boolean;
  readonly canSubscribe: boolean;
};

export function createOfflineMeetingEntry(json?: Partial<OfflineMeetingEntry>): OfflineMeetingEntry {
  const base = createSubscribableEntryBase(json, 'offlineMeeting');
  return makeAutoObservable({
    ...base,
    activityIdentifier: json?.activityIdentifier,
    data: json?.data ?? {},
    language: json?.language ?? '',
    previewTitle: (json?.previewTitle ?? []).map((l: any) => createLocalizedValue(l)),
    previewDescription: (json?.previewDescription ?? []).map((l: any) => createLocalizedValue(l)),
    minParticipants: json?.minParticipants ?? 0,
    maxParticipants: json?.maxParticipants ?? 0,
    subscriberCount: json?.subscriberCount ?? 0,
    registrationFields: (json?.registrationFields ?? []).map((f: any) => createFormField(f)),
    location: {
      id: (json?.location as any)?.id ?? '',
      name: (json?.location as any)?.name ?? '',
      address: (json?.location as any)?.address ?? '',
    },
    registerUntilDate: json?.registerUntilDate ?? null,
    datePairs: (json?.datePairs ?? []).map((dp: any) => ({
      startDate: dp.startDate,
      endDate: dp.endDate,
      cancelStatus: dp.cancelStatus,
    })),

    get remainingSeats(): number {
      return Math.max(0, this.maxParticipants - this.subscriberCount);
    },

    get hasRemainingSeats(): boolean {
      if (this.maxParticipants === 0) return true;
      return this.remainingSeats > 0;
    },

    get isMeetingOver(): boolean {
      const now = dayjs();
      if (this.datePairs?.length) {
        const lastDatePair = this.datePairs[this.datePairs.length - 1];
        return now.isAfter(dayjs(lastDatePair.endDate));
      }
      const endDate = this.nextDate?.add(this.dayjsDuration);
      return endDate ? now.isAfter(endDate) : false;
    },

    get canSubscribe(): boolean {
      const registrationOpen = !this.registerUntilDate || dayjs(this.registerUntilDate).isAfter(dayjs());
      return !this.isMeetingOver && registrationOpen;
    },
  });
}

export const OfflineMeetingEntryApi = {
  find(request: Omit<ExploreEntryQueryRequest, 'type'>): Promise<OfflineMeetingEntry[]> {
    return HttpBackend.get(`/coach/explore/v3/entries`, v1ParamsToV3Params({ ...request, type: 'offlineMeeting' })).then((res) =>
      (res ?? []).map((r: any) => createOfflineMeetingEntry(r)),
    );
  },

  findOne(id: string): Promise<OfflineMeetingEntry> {
    return HttpBackend.get(`/coach/explore/v3/entries/${id}`).then((res) => createOfflineMeetingEntry(res));
  },
};
